import { useState, useMemo } from 'react';
import useScrollReveal from '../../hooks/useScrollReveal';
import { useGlobal } from '../../context/GlobalContext';
import { formatPriceText, isPriceKey, toNumericPriceValue } from '../../lib/currency';
import { isEnglishLocale } from '../../lib/locale';
import styles from './AvailabilityList.module.css';
import iconCotizar from '../../assets/icons/icon_cotizar.svg';
import iconCorreo from '../../assets/icons/icon_correo.svg';
import iconFlechaArriba from '../../assets/icons/icon_flecha_arriba.svg';
import iconFlechaAbajo from '../../assets/icons/icon_flecha_abajo.svg';

const MAIN_FEATURE_GROUPS = [
  { id: 'price', patterns: ['precio', 'price'] },
  { id: 'area', patterns: ['total'] },
  { id: 'type', patterns: ['tipo', 'type'] },
];

// Features que sí necesita el front (p.ej. para mandarlas al crear un Lead)
// pero que no deben verse en la lista de disponibilidad.
const HIDDEN_FEATURE_KEYS = ['idproducto'];

// Features de metros cuadrados: se les agrega el sufijo "m²" al mostrarlas.
const AREA_FEATURE_PATTERNS = ['area total', 'area interior', 'terraza', 'balcon'];

function isAreaFeatureKey(key) {
  return AREA_FEATURE_PATTERNS.some((pattern) => key.includes(pattern));
}

function normalizeKey(str) {
  return (str || '').trim().toLowerCase();
}

function getFeatureGroupIndex(key) {
  return MAIN_FEATURE_GROUPS.findIndex(({ patterns }) => patterns.some((pattern) => key.includes(pattern)));
}

function classifyFeatures(features, pageTitulo, mostrarPrecio = true, locale = 'es') {
  const main = [];
  const secondary = [];
  let hasTipo = false;
  for (const f of features) {
    const key = normalizeKey(f.atributo);
    if (HIDDEN_FEATURE_KEYS.includes(key)) {
      continue;
    }
    const groupIndex = getFeatureGroupIndex(key);
    if (!mostrarPrecio && isPriceKey(key)) {
      continue;
    }
    if (groupIndex !== -1) {
      main.push(f);
      if (key.includes('tipo') || key.includes('type')) hasTipo = true;
    } else {
      secondary.push(f);
    }
  }
  if (!hasTipo && pageTitulo) {
    main.push({ atributo: isEnglishLocale(locale) ? 'Type' : 'Tipo', valor: pageTitulo });
  }
  main.sort((a, b) => {
    const ai = getFeatureGroupIndex(normalizeKey(a.atributo));
    const bi = getFeatureGroupIndex(normalizeKey(b.atributo));
    return ai - bi;
  });
  return { main, secondary };
}

const SORTABLE_COLUMN_COUNT = 3;
const CURRENCY_STRIP_REGEX = /^\s*(desde|starting at)\s+/i;
const CURRENCY_CODE_STRIP_REGEX = /\b(?:mxn|usd)\b/gi;
const NUMERIC_LOOKS_REGEX = /^-?[\d.,\s]+$/;
const DATE_DMY_REGEX = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
const DATE_YMD_REGEX = /^(\d{4})-(\d{1,2})-(\d{1,2})$/;

function isEmptySortValue(value) {
  if (value === null || value === undefined) return true;
  const str = String(value).trim();
  return str === '' || str === '-' || str === '—';
}

function looksNumeric(value) {
  const stripped = String(value ?? '')
    .replace(CURRENCY_STRIP_REGEX, '')
    .replace(CURRENCY_CODE_STRIP_REGEX, '')
    .replace(/\$/g, '')
    .trim();
  return stripped.length > 0 && NUMERIC_LOOKS_REGEX.test(stripped);
}

function parseDateValue(value) {
  const str = String(value ?? '').trim();
  let match = str.match(DATE_DMY_REGEX);
  if (match) {
    const [, day, month, year] = match;
    return Date.UTC(Number(year), Number(month) - 1, Number(day));
  }
  match = str.match(DATE_YMD_REGEX);
  if (match) {
    const [, year, month, day] = match;
    return Date.UTC(Number(year), Number(month) - 1, Number(day));
  }
  return null;
}

function compareSortValues(a, b, direction) {
  const aEmpty = isEmptySortValue(a);
  const bEmpty = isEmptySortValue(b);
  if (aEmpty && bEmpty) return 0;
  if (aEmpty) return 1;
  if (bEmpty) return -1;

  let result;
  if (looksNumeric(a) && looksNumeric(b)) {
    result = toNumericPriceValue(a) - toNumericPriceValue(b);
  } else {
    const aDate = parseDateValue(a);
    const bDate = parseDateValue(b);
    if (aDate !== null && bDate !== null) {
      result = aDate - bDate;
    } else {
      result = String(a).localeCompare(String(b), 'es', { sensitivity: 'base', numeric: true });
    }
  }

  return direction === 'desc' ? -result : result;
}

function getSortableValue(item, index, columnHeaders, pageTitulo, mostrarPrecio, locale) {
  if (index === 0) return item?.titulo;

  const { main } = classifyFeatures(item?.features || [], pageTitulo, mostrarPrecio, locale);
  const headerLabel = normalizeKey(columnHeaders[index]);
  const byName = main.find((f) => normalizeKey(f.atributo) === headerLabel);
  return byName ? byName.valor : main[index - 1]?.valor;
}

const AvailabilityList = ({
  data,
  items = [],
  pageTitulo = '',
  cotizadorConfig = null,
  onOpenCotizador,
}) => {
  const { companyInfo, locale } = useGlobal();
  const isEn = isEnglishLocale(locale);
  const titulo = data?.titulo;
  const disclaimer = typeof data?.disclaimer === 'string' && data.disclaimer.trim()
    ? data.disclaimer.trim()
    : (isEn
      ? '*Prices and prototype information are subject to change without prior notice.'
      : '*Los precios e informacion del prototipo estan sujetos a cambios sin previo aviso.');
  const tipoSingular = data?.tipoPropiedad || (isEn ? 'property' : 'propiedad');
  const tipoPlural = data?.tipoPropiedadPlural || (isEn ? 'properties' : 'propiedades');
  const urlContacto = typeof data?.urlContacto === 'string' ? data.urlContacto.trim() : '';
  const interestLabel = typeof data?.textoBotonInteresa === 'string'
    ? data.textoBotonInteresa.trim()
    : '';
  const interesaUseWhatsapp = data?.interesaUseWhatsapp === true;
  const interesaWhatsappNumber = data?.interesaWhatsappNumber || '';
  const interesaWhatsappMessage = data?.interesaWhatsappMessage || '';
  const mostrarPrecio = data?.mostrarPrecio !== false;
  const perPageRaw = parseInt(data?.itemsPorPagina || '0', 10);
  const showAll = perPageRaw <= 0;
  const perPage = showAll ? items.length || 1 : perPageRaw;
  const [currentPage, setCurrentPage] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [slideDir, setSlideDir] = useState('');
  const [sortConfig, setSortConfig] = useState({ index: 0, direction: 'asc' });
  const [ref, isVisible] = useScrollReveal({ threshold: 0.1 });

  // Derive column headers from the first item's main features
  const columnHeaders = useMemo(() => {
    const firstItem = items[0];
    const { main: firstMain } = classifyFeatures(firstItem?.features || [], pageTitulo, mostrarPrecio, locale);
    const headers = [isEn ? 'Name' : 'Nombre', ...firstMain.map((f) => f.atributo)];
    while (headers.length < 4) headers.push('');
    return headers;
  }, [items, pageTitulo, mostrarPrecio, locale, isEn]);

  const sortedItems = useMemo(() => {
    if (sortConfig.index === null) return items;

    const withValues = items.map((item) => ({
      item,
      value: getSortableValue(item, sortConfig.index, columnHeaders, pageTitulo, mostrarPrecio, locale),
    }));
    withValues.sort((a, b) => compareSortValues(a.value, b.value, sortConfig.direction));
    return withValues.map((entry) => entry.item);
  }, [items, sortConfig, columnHeaders, pageTitulo, mostrarPrecio, locale]);

  const handleSort = (index) => {
    if (index >= SORTABLE_COLUMN_COUNT) return;
    setSortConfig((prev) => (
      prev.index === index
        ? { index, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { index, direction: 'asc' }
    ));
    setCurrentPage(0);
  };

  const totalPages = Math.ceil(items.length / perPage);
  const pageItems = useMemo(
    () => sortedItems.slice(currentPage * perPage, (currentPage + 1) * perPage),
    [sortedItems, currentPage, perPage]
  );

  // El primer item de cada página empieza expandido
  const defaultExpandedId = useMemo(() => {
    const first = pageItems[0];
    return first ? (first.id ?? first.documentId) : null;
  }, [pageItems]);

  const [expandedState, setExpandedState] = useState(() => ({
    page: 0,
    itemId: defaultExpandedId,
  }));

  const expandedId = expandedState.page === currentPage
    ? expandedState.itemId
    : defaultExpandedId;

  const toggleItem = (itemId) => {
    setExpandedState((prev) => ({
      page: currentPage,
      itemId: prev.page === currentPage && prev.itemId === itemId ? null : itemId,
    }));
  };

  const handlePageChange = (newPage, direction) => {
    if (isAnimating || newPage < 0 || newPage >= totalPages) return;
    setSlideDir(direction);
    setIsAnimating(true);
    setTimeout(() => {
      setCurrentPage(newPage);
      setSlideDir(direction === 'left' ? 'enter-right' : 'enter-left');
      setTimeout(() => {
        setIsAnimating(false);
        setSlideDir('');
      }, 300);
    }, 250);
  };

  if (!items.length) return null;

  const getItemId = (item) => item?.id ?? item?.documentId;

  const resultLabel = isEn ? 'found' : 'encontrados';
  const subtitulo = `${items.length} ${items.length === 1 ? tipoSingular : tipoPlural} ${resultLabel}`;
  const hasCotizador = Boolean(cotizadorConfig);
  const showInterestButton = Boolean(urlContacto) || interesaUseWhatsapp;
  const quoteLabel = cotizadorConfig?.textoBotonCotizar || (isEn ? 'Quote' : 'Cotizar');

  const formatFeatureValue = (feature) => {
    if (isPriceKey(feature?.atributo)) {
      return formatPriceText(feature?.valor, companyInfo, locale) || '—';
    }

    const rawValue = feature?.valor;
    if (!rawValue) return '—';

    if (isAreaFeatureKey(normalizeKey(feature?.atributo)) && looksNumeric(rawValue)) {
      return `${rawValue} m²`;
    }

    return rawValue;
  };

  const handleCotizadorClick = (event, item) => {
    event.stopPropagation();

    onOpenCotizador?.(item);
  };

  const getFallbackWhatsappNumber = () => {
    const fromSocial = companyInfo?.socialMedia?.find((entry) => entry?.platform === 'whatsapp');
    if (fromSocial?.url) {
      const match = fromSocial.url.match(/(\d[\d\s\-+()]+)/);
      if (match) return match[1];
    }

    const fromContact = companyInfo?.contactInfo?.find((entry) => entry?.type === 'whatsapp');
    if (fromContact?.value) return fromContact.value;

    return null;
  };

  const buildWhatsappHref = (rawNumber, message, itemTitle) => {
    const number = (rawNumber || '').replace(/\D/g, '');
    if (!number) return null;

    const base = `https://wa.me/${number}`;
    const messageParts = [];

    if (message && message.trim()) {
      messageParts.push(message.trim());
    }

    if (itemTitle && itemTitle.trim()) {
      messageParts.push(itemTitle.trim());
    }

    if (messageParts.length > 0) {
      return `${base}?text=${encodeURIComponent(messageParts.join('\n\n'))}`;
    }

    return base;
  };

  return (
    <div
      ref={ref}
      className={`${styles.availability} ${isVisible ? styles['availability--visible'] : ''}`}
    >
      <div className={styles.availability__inner}>
      {titulo && (
        <h2 className={styles.availability__title}>{titulo}</h2>
      )}
      <p className={styles.availability__subtitle}>{subtitulo}</p>

      {/* Table header */}
      <div className={styles.availability__header}>
        {columnHeaders.map((header, i) => {
          const isSortable = i < SORTABLE_COLUMN_COUNT;
          const isActive = isSortable && sortConfig.index === i;
          return (
            <div
              key={i}
              className={`${styles.availability__headerCell} ${isSortable ? styles['availability__headerCell--sortable'] : ''}`}
              onClick={isSortable ? () => handleSort(i) : undefined}
            >
              {header}
              {header && (
                <span className={styles.availability__sortIcon}>
                  <img
                    src={iconFlechaArriba}
                    alt={isEn ? 'Sort ascending' : 'Orden ascendente'}
                    title={isEn ? 'Sort ascending' : 'Orden ascendente'}
                    className={`${styles.availability__sortArrow} ${isActive && sortConfig.direction === 'asc' ? styles['availability__sortArrow--active'] : ''}`}
                    aria-hidden="true"
                  />
                  <img
                    src={iconFlechaAbajo}
                    alt={isEn ? 'Sort descending' : 'Orden descendente'}
                    title={isEn ? 'Sort descending' : 'Orden descendente'}
                    className={`${styles.availability__sortArrow} ${isActive && sortConfig.direction === 'desc' ? styles['availability__sortArrow--active'] : ''}`}
                    aria-hidden="true"
                  />
                </span>
              )}
            </div>
          );
        })}
      </div>

      {/* Items list */}
      <div
        className={[
          styles.availability__list,
          slideDir === 'left' ? styles['availability__list--exitLeft'] : '',
          slideDir === 'right' ? styles['availability__list--exitRight'] : '',
          slideDir === 'enter-left' ? styles['availability__list--enterLeft'] : '',
          slideDir === 'enter-right' ? styles['availability__list--enterRight'] : '',
        ].filter(Boolean).join(' ')}
      >
        {pageItems.map((item) => {
          const itemId = getItemId(item);
          const isExpanded = expandedId === itemId;
          const { main, secondary } = classifyFeatures(item.features || [], pageTitulo, mostrarPrecio, locale);
          const interesaWhatsappHref = buildWhatsappHref(
            interesaWhatsappNumber || getFallbackWhatsappNumber(),
            interesaWhatsappMessage,
            item.titulo
          );
          const interesaHref = interesaUseWhatsapp && interesaWhatsappHref
            ? interesaWhatsappHref
            : (urlContacto
              ? `${urlContacto}${urlContacto.includes('?') ? '&' : '?'}unidad=${encodeURIComponent(item.titulo)}`
              : undefined);
          const interesaIsWhatsapp = interesaUseWhatsapp && Boolean(interesaWhatsappHref);

          return (
            <div
              key={itemId}
              className={`${styles.availability__item} ${isExpanded ? styles['availability__item--expanded'] : ''}`}
            >
              {/* Main row */}
              <div
                className={styles.availability__row}
                onClick={() => toggleItem(itemId)}
              >
                <div className={styles.availability__cell}>
                  <span className={styles.availability__itemName}>
                    {item.titulo || '—'}
                  </span>
                </div>
                {main.map((f, i) => (
                  <div key={i} className={styles.availability__cell}>
                    {formatFeatureValue(f)}
                  </div>
                ))}
                {/* Pad empty cells if not enough main features */}
                {Array.from({ length: Math.max(0, 3 - main.length) }).map((_, i) => (
                  <div key={`pad-${i}`} className={styles.availability__cell}>—</div>
                ))}
                <div className={styles.availability__cellActions}>
                  {hasCotizador && (
                    <button
                      className={styles.availability__btnCotizar}
                      title={quoteLabel}
                      onClick={(event) => handleCotizadorClick(event, item)}
                      type="button"
                    >
                      <img src={iconCotizar} alt={quoteLabel} title={quoteLabel} className={styles.availability__btnIcon} aria-hidden="true" />
                      {quoteLabel}
                    </button>
                  )}
                  {showInterestButton && (
                    <a
                      className={[
                        styles.availability__btnInteresa,
                        !interestLabel ? styles['availability__btnInteresa--iconOnly'] : '',
                      ].filter(Boolean).join(' ')}
                      title={interestLabel || undefined}
                      aria-label={interestLabel || undefined}
                      href={interesaHref}
                      onClick={(e) => e.stopPropagation()}
                      target={interesaIsWhatsapp ? '_blank' : undefined}
                      rel={interesaIsWhatsapp ? 'noopener noreferrer' : undefined}
                    >
                      <img src={iconCorreo} alt="" className={styles.availability__btnIcon} aria-hidden="true" />
                      {interestLabel}
                    </a>
                  )}
                </div>
                <div className={styles.availability__chevron}>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    className={`${styles.availability__chevronIcon} ${isExpanded ? styles['availability__chevronIcon--open'] : ''}`}
                  >
                    <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
              </div>

              {/* Expandable detail */}
              <div
                className={`${styles.availability__detail} ${isExpanded ? styles['availability__detail--open'] : ''}`}
              >
                <div className={styles.availability__detailInner}>
                  {secondary.map((f, i) => (
                    <div key={i} className={styles.availability__detailItem}>
                      <span className={styles.availability__detailLabel}>{f.atributo}</span>
                      <span className={styles.availability__detailValue}>{formatFeatureValue(f)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer: disclaimer + pagination */}
      <div className={styles.availability__footer}>
        <p className={styles.availability__disclaimer}>
          {disclaimer}
        </p>
        {!showAll && totalPages > 1 && (
        <div className={styles.availability__pagination}>
          <span className={styles.availability__pageInfo}>
            {currentPage + 1}–{totalPages}
          </span>
          <button
            className={styles.availability__pageBtn}
            onClick={() => handlePageChange(currentPage - 1, 'right')}
            disabled={currentPage === 0}
            aria-label={isEn ? 'Previous' : 'Anterior'}
          >
            ‹
          </button>
          <button
            className={styles.availability__pageBtn}
            onClick={() => handlePageChange(currentPage + 1, 'left')}
            disabled={currentPage >= totalPages - 1}
            aria-label={isEn ? 'Next' : 'Siguiente'}
          >
            ›
          </button>
        </div>
        )}
      </div>
      </div>
    </div>
  );
};

export default AvailabilityList;
